// Smart Daara - Subscription & Payment Page
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { tenantAPI, paymentAPI, subscriptionAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  CreditCard, Check, ArrowRight, Calendar, AlertTriangle,
  Loader2, Crown, Zap, Building2, Clock, Smartphone, Sparkles, Percent
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../../components/ui/dialog';

export default function SubscriptionPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [tenant, setTenant] = useState(null);
  const [physicalPlan, setPhysicalPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(null);
  // Provider picker
  const [providerModalOpen, setProviderModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  // OM QR modal
  const [omModal, setOmModal] = useState(null); // {paymentId, qrBase64, deeplinkOm, deeplinkMaxit, amount}
  const [omPolling, setOmPolling] = useState(false);
  // Custom duration modal
  const [customDurationOpen, setCustomDurationOpen] = useState(false);
  const [customDurationPlan, setCustomDurationPlan] = useState(null); // planId
  const [customMonths, setCustomMonths] = useState(1);
  const isMobile = typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  useEffect(() => {
    loadTenantData();
  }, []);

  const loadTenantData = async () => {
    try {
      const response = await tenantAPI.get(user?.tenant_id);
      setTenant(response.data);
      if (user?.school_type === 'physical') {
        const plansRes = await subscriptionAPI.listPlans({ school_type: 'physical' });
        setPhysicalPlan(plansRes.data?.[0] || null);
      }
    } catch (error) {
      console.error('Error loading tenant:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoading(false);
    }
  };

  const virtualPlans = [
    {
      id: 'starter',
      name: 'Starter',
      description: language === 'fr' 
        ? 'Pour les petites écoles qui débutent'
        : 'For small schools just starting',
      monthlyPrice: 30000,
      yearlyPrice: 300000,
      features: [
        language === 'fr' ? '1 administrateur' : '1 administrator',
        language === 'fr' ? '5 managers' : '5 managers',
        language === 'fr' ? '10 enseignants' : '10 teachers',
        language === 'fr' ? '10 classes' : '10 classes',
        language === 'fr' ? 'Support email' : 'Email support'
      ],
      icon: Building2,
      color: 'border-slate-200 bg-white'
    },
    {
      id: 'pro',
      name: 'Pro',
      description: language === 'fr' 
        ? 'Pour les écoles en croissance'
        : 'For growing schools',
      monthlyPrice: 60000,
      yearlyPrice: 600000,
      features: [
        language === 'fr' ? '1 administrateur' : '1 administrator',
        language === 'fr' ? '5 managers' : '5 managers',
        language === 'fr' ? '25 enseignants' : '25 teachers',
        language === 'fr' ? '25 classes' : '25 classes',
        language === 'fr' ? 'Support prioritaire' : 'Priority support'
      ],
      icon: Zap,
      color: 'border-amber-200 bg-amber-50',
      popular: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      description: language === 'fr' 
        ? 'Pour les grandes institutions'
        : 'For large institutions',
      monthlyPrice: 100000,
      yearlyPrice: 1000000,
      features: [
        language === 'fr' ? '3 administrateurs' : '3 administrators',
        language === 'fr' ? '10 managers' : '10 managers',
        language === 'fr' ? 'Enseignants illimités' : 'Unlimited teachers',
        language === 'fr' ? 'Classes illimitées' : 'Unlimited classes',
        language === 'fr' ? 'Support dédié 24/7' : 'Dedicated 24/7 support'
      ],
      icon: Crown,
      color: 'border-purple-200 bg-purple-50'
    }
  ];

  // Physical schools: single plan, prices read from Super Admin configuration
  const plans = user?.school_type === 'physical'
    ? [{
        id: 'physical',
        name: language === 'fr' ? (physicalPlan?.name_fr || 'École physique') : (physicalPlan?.name || 'Physical School'),
        description: language === 'fr'
          ? 'Gestion d\'établissement complète, sans limite'
          : 'Full school management, unlimited',
        monthlyPrice: physicalPlan?.monthly_price ?? 75000,
        yearlyPrice: physicalPlan?.yearly_price ?? 750000,
        features: language === 'fr'
          ? ['Classes, niveaux et matières illimités', 'Emplois du temps automatiques', 'Notes & bulletins', 'Présences & vie scolaire', 'Frais de scolarité']
          : ['Unlimited classes, levels and subjects', 'Automatic timetables', 'Grades & report cards', 'Attendance', 'School fees'],
        icon: Building2,
        color: 'border-sky-200 bg-sky-50',
        popular: true
      }]
    : virtualPlans;

  const openProviderPicker = (planId, billingPeriod, durationMonths = null) => {
    setSelectedPlan({ planId, billingPeriod, durationMonths });
    setProviderModalOpen(true);
  };

  const openCustomDurationPicker = (planId) => {
    setCustomDurationPlan(planId);
    setCustomMonths(1);
    setCustomDurationOpen(true);
  };

  const confirmCustomDuration = () => {
    const months = Math.min(24, Math.max(1, parseInt(customMonths || 1, 10)));
    setCustomDurationOpen(false);
    openProviderPicker(customDurationPlan, 'custom', months);
  };

  const handlePayment = async (provider) => {
    if (!selectedPlan) return;
    const { planId, billingPeriod, durationMonths } = selectedPlan;
    setProcessingPayment(`${planId}-${billingPeriod}`);
    setProviderModalOpen(false);
    try {
      const payload = {
        tenant_id: user?.tenant_id,
        plan_type: planId,
        billing_period: billingPeriod,
        provider: provider,
      };
      if (billingPeriod === 'custom' && durationMonths) {
        payload.duration_months = durationMonths;
      }
      const response = await paymentAPI.createInvoice(payload);

      const data = response.data;
      try { localStorage.setItem('lastPaymentId', data.payment_id); } catch (e) { /* ignore */ }

      if (provider === 'wave') {
        // Wave: redirect to launch URL
        const checkoutUrl = data.checkout_url || data.invoice_url;
        if (checkoutUrl) { window.location.href = checkoutUrl; return; }
        toast.error(language === 'fr' ? 'URL Wave manquante' : 'Missing Wave URL');
      } else {
        // OM: open QR modal with deepLinks
        if (data.qr_code_base64) {
          setOmModal({
            paymentId: data.payment_id,
            qrBase64: data.qr_code_base64,
            deeplinkOm: data.deeplink_om,
            deeplinkMaxit: data.deeplink_maxit,
            amount: data.amount,
            validity: data.validity_seconds,
          });
          // Start polling
          startOmPolling(data.payment_id);
        } else {
          toast.error(language === 'fr' ? 'QR code Orange Money manquant' : 'OM QR code missing');
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      const detail = error.response?.data?.detail;
      const detailMsg = typeof detail === 'string'
        ? detail
        : (detail?.provider_error?.message || detail?.message || null);
      toast.error(detailMsg || (language === 'fr' ? 'Erreur de paiement — réessayez ou contactez le support' : 'Payment error — retry or contact support'));
    } finally {
      setProcessingPayment(null);
      setSelectedPlan(null);
    }
  };

  // Polling for OM QR payment status
  const startOmPolling = (paymentId) => {
    setOmPolling(true);
    let attempts = 0;
    const MAX_ATTEMPTS = 90; // ~5min with 3.3s interval
    const intervalId = setInterval(async () => {
      attempts += 1;
      try {
        const res = await paymentAPI.getStatus(paymentId);
        const s = res.data?.status;
        if (s === 'completed') {
          clearInterval(intervalId);
          setOmPolling(false);
          toast.success(language === 'fr' ? '✅ Paiement reçu ! Abonnement activé.' : 'Payment received! Subscription activated.');
          try { localStorage.removeItem('lastPaymentId'); } catch (e) { /* ignore */ }
          setTimeout(() => { setOmModal(null); window.location.reload(); }, 2000);
        } else if (s === 'failed' || s === 'cancelled') {
          clearInterval(intervalId);
          setOmPolling(false);
          toast.error(language === 'fr' ? 'Paiement échoué ou annulé' : 'Payment failed or cancelled');
        } else if (attempts >= MAX_ATTEMPTS) {
          clearInterval(intervalId);
          setOmPolling(false);
          toast(language === 'fr' ? 'Délai dépassé. Vérifiez votre app OM.' : 'Timeout. Check your OM app.');
        }
      } catch (e) {
        // ignore transient errors
      }
    }, 3300);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR').format(price);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
        day: 'numeric', month: 'long', year: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  const getDaysRemaining = () => {
    if (!tenant?.deactivation_date || tenant?.is_free) return null;
    try {
      const date = new Date(tenant.deactivation_date);
      const now = new Date();
      return Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    } catch {
      return null;
    }
  };

  const daysRemaining = getDaysRemaining();
  const isExpired = daysRemaining !== null && daysRemaining <= 0;
  const isExpiringSoon = daysRemaining !== null && daysRemaining <= 7 && daysRemaining > 0;
  const currentPlan = tenant?.subscription_plan;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="subscription-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-outfit font-bold text-slate-900">
          {language === 'fr' ? 'Abonnement' : 'Subscription'}
        </h1>
        <p className="text-slate-500">
          {language === 'fr' 
            ? 'Gérez votre abonnement et vos paiements'
            : 'Manage your subscription and payments'
          }
        </p>
      </div>

      {/* Current Status Card */}
      {tenant && (
        <Card className={`border-0 shadow-sm ${
          isExpired ? 'bg-red-50 border-red-200' : 
          isExpiringSoon ? 'bg-amber-50 border-amber-200' : ''
        }`}>
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-semibold text-slate-900">
                    {tenant.name}
                  </h2>
                  <Badge className={
                    tenant.is_free ? 'bg-emerald-100 text-emerald-700' :
                    currentPlan === 'enterprise' ? 'bg-purple-100 text-purple-700' :
                    currentPlan === 'pro' ? 'bg-amber-100 text-amber-700' :
                    'bg-slate-100 text-slate-700'
                  }>
                    {tenant.is_free ? (language === 'fr' ? 'Gratuit' : 'Free') :
                     currentPlan?.charAt(0).toUpperCase() + currentPlan?.slice(1)}
                  </Badge>
                </div>
                
                {!tenant.is_free && (
                  <div className="flex items-center gap-4 mt-2">
                    <div className="flex items-center gap-1.5 text-sm">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span className={isExpired ? 'text-red-600 font-medium' : 'text-slate-600'}>
                        {language === 'fr' ? 'Expire le' : 'Expires on'}: {formatDate(tenant.deactivation_date)}
                      </span>
                    </div>
                    
                    {daysRemaining !== null && (
                      <span className={`text-sm font-medium ${
                        isExpired ? 'text-red-600' : 
                        isExpiringSoon ? 'text-amber-600' : 
                        'text-emerald-600'
                      }`}>
                        {isExpired 
                          ? (language === 'fr' ? 'Expiré!' : 'Expired!')
                          : `${daysRemaining} ${language === 'fr' ? 'jours restants' : 'days remaining'}`
                        }
                      </span>
                    )}
                  </div>
                )}
              </div>

              {(isExpired || isExpiringSoon) && (
                <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                  isExpired ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-medium">
                    {isExpired 
                      ? (language === 'fr' ? 'Abonnement expiré - Renouvelez maintenant' : 'Subscription expired - Renew now')
                      : (language === 'fr' ? 'Expire bientôt - Renouvelez' : 'Expiring soon - Renew')
                    }
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pricing Plans */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-4">
          {language === 'fr' ? 'Choisissez votre plan' : 'Choose your plan'}
        </h2>
        
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan) => {
            const isCurrentPlan = currentPlan === plan.id && !tenant?.is_free;
            const canUpgrade = plans.findIndex(p => p.id === currentPlan) < plans.findIndex(p => p.id === plan.id);
            
            return (
              <Card 
                key={plan.id} 
                className={`relative ${plan.color} ${
                  plan.popular ? 'ring-2 ring-amber-400' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-amber-500 text-white">
                      {language === 'fr' ? 'Populaire' : 'Popular'}
                    </Badge>
                  </div>
                )}
                
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-2">
                    <plan.icon className={`w-5 h-5 ${
                      plan.id === 'enterprise' ? 'text-purple-600' :
                      plan.id === 'pro' ? 'text-amber-600' :
                      'text-slate-600'
                    }`} />
                    <CardTitle className="font-outfit">{plan.name}</CardTitle>
                  </div>
                  <CardDescription>{plan.description}</CardDescription>
                </CardHeader>
                
                <CardContent className="space-y-6">
                  {/* Pricing */}
                  <div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-slate-900">
                        {formatPrice(plan.monthlyPrice)}
                      </span>
                      <span className="text-slate-500">FCFA/mois</span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">
                      {language === 'fr' ? 'ou' : 'or'} {formatPrice(plan.yearlyPrice)} FCFA/{language === 'fr' ? 'an' : 'year'}
                    </p>
                  </div>

                  {/* Features */}
                  <ul className="space-y-2">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                        <Check className="w-4 h-4 text-emerald-600" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* Action Buttons */}
                  <div className="space-y-2">
                    {isCurrentPlan && !isExpired ? (
                      <div className="space-y-2">
                        <Button 
                          className="w-full bg-emerald-700 hover:bg-emerald-800"
                          onClick={() => openProviderPicker(plan.id, 'monthly')}
                          disabled={processingPayment !== null}
                        >
                          {processingPayment === `${plan.id}-monthly` && (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          )}
                          <Clock className="w-4 h-4 mr-2" />
                          {language === 'fr' ? 'Renouveler (1 mois)' : 'Renew (1 month)'}
                        </Button>
                        <Button 
                          variant="outline"
                          className="w-full"
                          onClick={() => openProviderPicker(plan.id, 'yearly')}
                          disabled={processingPayment !== null}
                        >
                          {processingPayment === `${plan.id}-yearly` && (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          )}
                          {language === 'fr' ? 'Renouveler (1 an) - Économisez 17%' : 'Renew (1 year) - Save 17%'}
                        </Button>
                        <Button
                          variant="ghost"
                          className="w-full text-emerald-700 hover:bg-emerald-50"
                          onClick={() => openCustomDurationPicker(plan.id)}
                          disabled={processingPayment !== null}
                          data-testid={`custom-duration-btn-${plan.id}`}
                        >
                          <Sparkles className="w-4 h-4 mr-2" />
                          {language === 'fr' ? 'Durée personnalisée…' : 'Custom duration…'}
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Button 
                          className={`w-full ${
                            plan.id === 'enterprise' ? 'bg-purple-600 hover:bg-purple-700' :
                            plan.id === 'pro' ? 'bg-amber-600 hover:bg-amber-700' :
                            'bg-emerald-700 hover:bg-emerald-800'
                          }`}
                          onClick={() => openProviderPicker(plan.id, 'monthly')}
                          disabled={processingPayment !== null}
                        >
                          {processingPayment === `${plan.id}-monthly` && (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          )}
                          <CreditCard className="w-4 h-4 mr-2" />
                          {canUpgrade 
                            ? (language === 'fr' ? 'Upgrade (Mensuel)' : 'Upgrade (Monthly)')
                            : (language === 'fr' ? 'Souscrire (Mensuel)' : 'Subscribe (Monthly)')
                          }
                        </Button>
                        <Button 
                          variant="outline"
                          className="w-full"
                          onClick={() => openProviderPicker(plan.id, 'yearly')}
                          disabled={processingPayment !== null}
                        >
                          {processingPayment === `${plan.id}-yearly` && (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          )}
                          {canUpgrade 
                            ? (language === 'fr' ? 'Upgrade (Annuel) - 17% off' : 'Upgrade (Yearly) - 17% off')
                            : (language === 'fr' ? 'Souscrire (Annuel) - 17% off' : 'Subscribe (Yearly) - 17% off')
                          }
                        </Button>
                        <Button
                          variant="ghost"
                          className="w-full text-emerald-700 hover:bg-emerald-50"
                          onClick={() => openCustomDurationPicker(plan.id)}
                          disabled={processingPayment !== null}
                          data-testid={`custom-duration-btn-${plan.id}`}
                        >
                          <Sparkles className="w-4 h-4 mr-2" />
                          {language === 'fr' ? 'Durée personnalisée…' : 'Custom duration…'}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Payment Info */}
      <Card className="border-0 shadow-sm bg-slate-50">
        <CardContent className="p-6">
          <h3 className="font-semibold text-slate-900 mb-2">
            {language === 'fr' ? 'Moyens de paiement acceptés' : 'Accepted payment methods'}
          </h3>
          <div className="flex flex-wrap gap-4 mt-3">
            <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border">
              <div className="w-8 h-8 bg-blue-500 rounded flex items-center justify-center">
                <span className="text-white text-xs font-bold">Wave</span>
              </div>
              <span className="text-sm">Wave</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border">
              <div className="w-8 h-8 bg-orange-500 rounded flex items-center justify-center">
                <span className="text-white text-xs font-bold">OM</span>
              </div>
              <span className="text-sm">Orange Money</span>
            </div>
          </div>
          <p className="text-sm text-slate-500 mt-4">
            {language === 'fr' 
              ? 'Paiements sécurisés via Wave & Orange Money. Vous serez redirigé vers la passerelle pour finaliser.'
              : 'Secure payments via Wave & Orange Money. You will be redirected to the gateway to complete.'
            }
          </p>
        </CardContent>
      </Card>

      {/* Payment Provider Modal */}
      <Dialog open={providerModalOpen} onOpenChange={setProviderModalOpen}>
        <DialogContent className="max-w-md" data-testid="payment-provider-modal">
          <DialogHeader>
            <DialogTitle>
              {language === 'fr' ? 'Choisissez votre moyen de paiement' : 'Choose your payment method'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? 'Vous serez redirigé vers la passerelle sécurisée pour finaliser le paiement.'
                : 'You will be redirected to the secure gateway to complete the payment.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 py-4">
            <button
              type="button"
              onClick={() => handlePayment('wave')}
              className="flex items-center justify-between p-4 border-2 border-slate-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
              data-testid="pay-with-wave-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold">
                  Wave
                </div>
                <div className="text-left">
                  <p className="font-semibold text-slate-900">Wave</p>
                  <p className="text-xs text-slate-500">{language === 'fr' ? 'Paiement instantané' : 'Instant payment'}</p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400" />
            </button>
            <button
              type="button"
              onClick={() => handlePayment('orange_money')}
              className="flex items-center justify-between p-4 border-2 border-slate-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors"
              data-testid="pay-with-om-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold text-xs">
                  OM
                </div>
                <div className="text-left">
                  <p className="font-semibold text-slate-900">Orange Money</p>
                  <p className="text-xs text-slate-500">{language === 'fr' ? 'Via #144#' : 'Via #144#'}</p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400" />
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProviderModalOpen(false)} data-testid="cancel-provider-pick">
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Orange Money QR Modal */}
      <Dialog open={!!omModal} onOpenChange={(open) => { if (!open) { setOmModal(null); setOmPolling(false); } }}>
        <DialogContent className="max-w-md" data-testid="om-qr-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-500 rounded flex items-center justify-center text-white font-bold text-xs">OM</div>
              {language === 'fr' ? 'Paiement Orange Money' : 'Orange Money Payment'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? `Montant : ${omModal?.amount?.toLocaleString('fr-FR')} FCFA`
                : `Amount: ${omModal?.amount?.toLocaleString('en-US')} XOF`}
            </DialogDescription>
          </DialogHeader>

          {omModal && (
            <div className="space-y-4 py-2">
              {/* Mobile: deep link CTA first */}
              {isMobile && (
                <div className="space-y-2">
                  <p className="text-sm text-slate-700 font-medium text-center">
                    {language === 'fr' ? 'Cliquez pour ouvrir votre application :' : 'Tap to open your app:'}
                  </p>
                  <a
                    href={omModal.deeplinkOm}
                    className="flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-semibold transition-colors"
                    data-testid="open-om-app-btn"
                  >
                    <Smartphone className="w-5 h-5" />
                    {language === 'fr' ? 'Ouvrir Orange Money' : 'Open Orange Money'}
                  </a>
                  {omModal.deeplinkMaxit && omModal.deeplinkMaxit !== omModal.deeplinkOm && (
                    <a
                      href={omModal.deeplinkMaxit}
                      className="flex items-center justify-center gap-2 w-full py-3 rounded-lg border-2 border-orange-500 hover:bg-orange-50 text-orange-700 font-semibold transition-colors"
                      data-testid="open-maxit-app-btn"
                    >
                      <Smartphone className="w-5 h-5" />
                      {language === 'fr' ? 'Ouvrir Maxit' : 'Open Maxit'}
                    </a>
                  )}
                  <p className="text-xs text-slate-500 text-center mt-2">
                    {language === 'fr' ? 'ou scannez ce QR code :' : 'or scan this QR code:'}
                  </p>
                </div>
              )}

              {/* QR code */}
              <div className="flex justify-center bg-white p-3 rounded-lg border border-slate-200">
                <img
                  src={`data:image/png;base64,${omModal.qrBase64}`}
                  alt="QR Code Orange Money"
                  className="w-56 h-56 object-contain"
                  data-testid="om-qr-image"
                />
              </div>

              {!isMobile && (
                <div className="space-y-2">
                  <p className="text-sm text-slate-700 text-center">
                    {language === 'fr' ? 'Scannez avec Orange Money ou Maxit' : 'Scan with Orange Money or Maxit'}
                  </p>
                  {omModal.deeplinkOm && (
                    <div className="flex gap-2">
                      <a
                        href={omModal.deeplinkOm}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 text-center py-2 rounded-md border border-orange-500 text-orange-700 hover:bg-orange-50 text-xs font-medium"
                      >
                        {language === 'fr' ? 'Lien OM' : 'OM link'}
                      </a>
                      {omModal.deeplinkMaxit && omModal.deeplinkMaxit !== omModal.deeplinkOm && (
                        <a
                          href={omModal.deeplinkMaxit}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 text-center py-2 rounded-md border border-orange-500 text-orange-700 hover:bg-orange-50 text-xs font-medium"
                        >
                          {language === 'fr' ? 'Lien Maxit' : 'Maxit link'}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Status */}
              <div className={`flex items-center justify-center gap-2 p-3 rounded-lg ${omPolling ? 'bg-blue-50 text-blue-700' : 'bg-slate-50 text-slate-600'}`}>
                {omPolling ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm font-medium">
                      {language === 'fr' ? 'En attente du paiement...' : 'Waiting for payment...'}
                    </span>
                  </>
                ) : (
                  <span className="text-sm">{language === 'fr' ? 'Prêt' : 'Ready'}</span>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => { setOmModal(null); setOmPolling(false); }}
              data-testid="close-om-modal-btn"
            >
              {language === 'fr' ? 'Fermer' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Duration Modal */}
      <Dialog open={customDurationOpen} onOpenChange={setCustomDurationOpen}>
        <DialogContent className="max-w-md" data-testid="custom-duration-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-600" />
              {language === 'fr' ? 'Durée personnalisée' : 'Custom duration'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr'
                ? 'Choisissez le nombre de mois à payer d\'avance. Bénéficiez de 2 mois offerts pour chaque année complète.'
                : 'Choose how many months to pay in advance. Get 2 free months for each full year.'}
            </DialogDescription>
          </DialogHeader>

          {(() => {
            const plan = plans.find(p => p.id === customDurationPlan);
            const months = Math.min(24, Math.max(1, parseInt(customMonths || 1, 10)));
            const paidMonths = Math.floor(months / 12) * 10 + (months % 12);
            const totalAmount = plan ? plan.monthlyPrice * paidMonths : 0;
            const fullAmount = plan ? plan.monthlyPrice * months : 0;
            const savings = fullAmount - totalAmount;

            return (
              <div className="space-y-4 py-2">
                {plan && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <plan.icon className="w-4 h-4 text-slate-500" />
                    <span>{language === 'fr' ? 'Plan sélectionné' : 'Selected plan'} :</span>
                    <span className="font-semibold text-slate-900">{plan.name}</span>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="custom-months-input">
                    {language === 'fr' ? 'Nombre de mois (1 à 24)' : 'Number of months (1 to 24)'}
                  </Label>
                  <Input
                    id="custom-months-input"
                    type="number"
                    min="1"
                    max="24"
                    value={customMonths}
                    onChange={(e) => setCustomMonths(e.target.value)}
                    data-testid="custom-months-input"
                  />
                  <div className="flex gap-2 flex-wrap">
                    {[1, 3, 6, 12, 18, 24].map(m => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setCustomMonths(m)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                          months === m
                            ? 'bg-emerald-600 text-white border-emerald-600'
                            : 'bg-white text-slate-600 border-slate-300 hover:border-emerald-500'
                        }`}
                        data-testid={`quick-month-${m}`}
                      >
                        {m} {language === 'fr' ? 'mois' : 'mo.'}
                      </button>
                    ))}
                  </div>
                </div>

                {plan && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">{language === 'fr' ? 'Prix mensuel' : 'Monthly price'}</span>
                      <span className="font-medium">{formatPrice(plan.monthlyPrice)} FCFA</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">{language === 'fr' ? 'Mois payés' : 'Months paid'}</span>
                      <span className="font-medium">
                        {paidMonths} / {months} {language === 'fr' ? 'mois' : 'mo.'}
                      </span>
                    </div>
                    {savings > 0 && (
                      <div className="flex justify-between text-sm text-emerald-700">
                        <span className="flex items-center gap-1">
                          <Percent className="w-3 h-3" />
                          {language === 'fr' ? 'Économies (2 mois offerts / an)' : 'Discount (2 free months / year)'}
                        </span>
                        <span className="font-semibold">- {formatPrice(savings)} FCFA</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-slate-300">
                      <span className="text-slate-900 font-semibold">
                        {language === 'fr' ? 'Total à payer' : 'Total to pay'}
                      </span>
                      <span className="text-lg font-bold text-emerald-700" data-testid="custom-total-amount">
                        {formatPrice(totalAmount)} FCFA
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomDurationOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button
              onClick={confirmCustomDuration}
              className="bg-emerald-700 hover:bg-emerald-800"
              data-testid="confirm-custom-duration-btn"
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Choisir le moyen de paiement' : 'Choose payment method'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
